import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getBrandTagById, getBrandTagList } from "@/api/system/tags";
import type { BrandTagListItemVO } from "@/api/system/tags/type";
import { isEnableStatusEnabled } from "@/types";

import { buildBrandSearchText } from "../utils";

/** useBrandSelect 入参 */
export interface UseBrandSelectParams {
  /** 当前选中的品牌 ID */
  value?: string | null;
  /** 是否禁用拉取 */
  disabled?: boolean;
  /** 是否仅展示启用品牌，默认 true */
  onlyEnabled?: boolean;
}

/**
 * 品牌下拉数据：首次打开拉全量列表，选中项缺失时按 ID 回填
 */
export const useBrandSelect = ({ value, disabled, onlyEnabled = true }: UseBrandSelectParams) => {
  // 品牌原始列表
  const [brands, setBrands] = useState<BrandTagListItemVO[]>([]);
  // 列表加载中
  const [loading, setLoading] = useState(false);
  // 是否已成功拉取过列表
  const [loaded, setLoaded] = useState(false);
  // 下拉内搜索关键字
  const [searchKeyword, setSearchKeyword] = useState("");
  const listRequestIdRef = useRef(0);

  /** 拉取品牌列表（全量，关键字由前端过滤） */
  const fetchBrandList = useCallback(async () => {
    const requestId = ++listRequestIdRef.current;
    setLoading(true);
    try {
      const res = await getBrandTagList();
      if (requestId !== listRequestIdRef.current) return;
      setBrands(res.data?.items ?? []);
      setLoaded(true);
    } catch {
      if (requestId !== listRequestIdRef.current) return;
      setBrands([]);
      setLoaded(true);
    } finally {
      if (requestId === listRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /** 选中 ID 不在当前列表时，按 ID 回填一项 */
  useEffect(() => {
    if (disabled || !value) return;
    if (brands.some((item) => item.id === value)) return;
    void (async () => {
      try {
        const res = await getBrandTagById(value);
        const detail = res.data;
        if (!detail) return;
        setBrands((prev) =>
          prev.some((item) => item.id === detail.id) ? prev : [detail, ...prev],
        );
      } catch {
        // 回填失败时保留当前 options，由 Select 展示 value
      }
    })();
  }, [brands, disabled, value]);

  /** 按启用状态与关键字过滤后的品牌列表 */
  const filteredBrands = useMemo(() => {
    let list = brands;
    if (onlyEnabled) {
      list = list.filter((item) => isEnableStatusEnabled(item.status));
    }
    const keyword = searchKeyword.trim().toLowerCase();
    if (keyword) {
      list = list.filter((item) => buildBrandSearchText(item).includes(keyword));
    }
    return list;
  }, [brands, onlyEnabled, searchKeyword]);

  /** antd Select options */
  const options = useMemo(
    () =>
      filteredBrands.map((brand) => ({
        label: brand.brandName,
        value: brand.id,
        brand,
      })),
    [filteredBrands],
  );

  /** 无数据 / 无匹配时的提示文案 */
  const notFoundContent = useMemo(() => {
    if (loading) return "加载中…";
    if (!loaded) return "加载中…";
    if (!brands.length) return "暂无品牌";
    if (!filteredBrands.length) return "无匹配项";
    return undefined;
  }, [brands.length, filteredBrands.length, loaded, loading]);

  /** 按 ID 取品牌行 */
  const getBrandById = useCallback((id: string) => brands.find((item) => item.id === id), [brands]);

  return {
    options,
    loading,
    loaded,
    notFoundContent,
    searchKeyword,
    setSearchKeyword,
    fetchBrandList,
    getBrandById,
  };
};
